import { useState, useCallback } from 'react';

/**
 * useRazorpay Hook
 * 
 * Manages the complete Razorpay payment flow:
 * 1. Load Razorpay SDK script dynamically
 * 2. Create order via backend API
 * 3. Open Razorpay checkout modal
 * 4. Verify payment via backend API
 * 5. Handle success/failure callbacks
 * 
 * Usage:
 *   const { initiatePayment, loading, error } = useRazorpay();
 *   
 *   const handleCheckout = async () => {
 *     const result = await initiatePayment({
 *       items: cartItems,
 *       shippingAddress: address,
 *       userInfo: { name, email, phone },
 *     });
 *     if (result.success) { // order confirmed }
 *   };
 */

const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

// ── Load Razorpay SDK ──
function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

// ── API Helper ──
async function apiCall(endpoint, options = {}) {
  const token = localStorage.getItem('gramaayam_token');
  const response = await fetch(`/api/v1${endpoint}`, {
    method: options.method || 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Request failed');
  }

  return data;
}

export function useRazorpay() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Initiate the complete payment flow
   * 
   * @param {Object} options
   * @param {Array}  options.items           - Cart items [{ product, quantity }]
   * @param {Object} options.shippingAddress - { name, phone, street, city, state, pincode }
   * @param {Object} options.userInfo        - { name, email, phone }
   * @param {string} [options.notes]         - Order notes
   * @param {string} [options.theme]         - Razorpay theme color
   * @returns {Promise<{ success, order, error? }>}
   */
  const initiatePayment = useCallback(async ({ items, shippingAddress, userInfo, notes, theme }) => {
    setLoading(true);
    setError(null);

    try {
      // ── Step 1: Load Razorpay SDK ──
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) {
        throw new Error('Failed to load Razorpay. Please check your internet connection.');
      }

      // ── Step 2: Create order via our API ──
      const orderResponse = await apiCall('/payments/create-order', {
        body: {
          items: items.map((i) => ({
            product: i._id || i.product,
            quantity: i.qty || i.quantity,
          })),
          shippingAddress,
          notes,
        },
      });

      const { order, razorpay } = orderResponse.data;

      // ── Step 3: Open Razorpay Checkout ──
      return new Promise((resolve) => {
        const options = {
          key: razorpay.keyId,
          amount: razorpay.amount,
          currency: razorpay.currency,
          name: 'Gramaayam',
          description: `Order #${order.orderNumber}`,
          image: '/logo192.png', // Your logo
          order_id: razorpay.orderId,
          prefill: {
            name: userInfo?.name || '',
            email: userInfo?.email || '',
            contact: userInfo?.phone || '',
          },
          notes: {
            orderNumber: order.orderNumber,
            orderId: order._id,
          },
          theme: {
            color: theme || '#2D5A3F',
          },
          modal: {
            ondismiss: async () => {
              // User closed checkout without paying
              try {
                await apiCall('/payments/failed', {
                  body: {
                    razorpay_order_id: razorpay.orderId,
                    error_code: 'USER_CANCELLED',
                    error_description: 'Checkout closed by user',
                  },
                });
              } catch (e) {
                console.error('Failed to record cancellation:', e);
              }

              setLoading(false);
              resolve({
                success: false,
                cancelled: true,
                order,
                error: 'Payment cancelled',
              });
            },
          },

          // ── Step 4: Payment Success Handler ──
          handler: async (response) => {
            try {
              const verifyResponse = await apiCall('/payments/verify', {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  orderId: order._id,
                },
              });

              setLoading(false);
              resolve({
                success: true,
                order: verifyResponse.data.order,
                paymentId: response.razorpay_payment_id,
              });
            } catch (verifyError) {
              setLoading(false);
              setError(verifyError.message);
              resolve({
                success: false,
                error: verifyError.message,
                order,
              });
            }
          },
        };

        const razorpayInstance = new window.Razorpay(options);

        // Handle payment failure at Razorpay level
        razorpayInstance.on('payment.failed', async (failedResponse) => {
          try {
            await apiCall('/payments/failed', {
              body: {
                razorpay_order_id: razorpay.orderId,
                error_code: failedResponse.error?.code,
                error_description: failedResponse.error?.description,
              },
            });
          } catch (e) {
            console.error('Failed to record payment failure:', e);
          }

          setLoading(false);
          setError(failedResponse.error?.description || 'Payment failed');
          resolve({
            success: false,
            error: failedResponse.error?.description || 'Payment failed',
            order,
            canRetry: true,
          });
        });

        razorpayInstance.open();
      });
    } catch (err) {
      setLoading(false);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Retry payment for a failed order
   */
  const retryPayment = useCallback(async ({ orderId, userInfo, theme }) => {
    setLoading(true);
    setError(null);

    try {
      const sdkLoaded = await loadRazorpayScript();
      if (!sdkLoaded) throw new Error('Failed to load Razorpay.');

      const retryResponse = await apiCall(`/payments/retry/${orderId}`);
      const { order, razorpay } = retryResponse.data;

      return new Promise((resolve) => {
        const options = {
          key: razorpay.keyId,
          amount: razorpay.amount,
          currency: razorpay.currency,
          name: 'Gramaayam',
          description: `Order #${order.orderNumber} (Retry)`,
          order_id: razorpay.orderId,
          prefill: {
            name: userInfo?.name || '',
            email: userInfo?.email || '',
            contact: userInfo?.phone || '',
          },
          theme: { color: theme || '#2D5A3F' },
          handler: async (response) => {
            try {
              const verifyResponse = await apiCall('/payments/verify', {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  orderId: order._id,
                },
              });
              setLoading(false);
              resolve({ success: true, order: verifyResponse.data.order });
            } catch (e) {
              setLoading(false);
              resolve({ success: false, error: e.message });
            }
          },
          modal: {
            ondismiss: () => {
              setLoading(false);
              resolve({ success: false, cancelled: true });
            },
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      });
    } catch (err) {
      setLoading(false);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, []);

  /**
   * Get payment status for an order
   */
  const getPaymentStatus = useCallback(async (orderId) => {
    try {
      const response = await apiCall(`/payments/status/${orderId}`, { method: 'GET' });
      return response.data;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, []);

  return {
    initiatePayment,
    retryPayment,
    getPaymentStatus,
    loading,
    error,
    clearError: () => setError(null),
  };
}
